import React, { useMemo } from "react";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
} from "recharts";
import {
    ValueType,
    NameType,
} from "recharts/types/component/DefaultTooltipContent";

interface DataItem {
    name: string;
    value: number;
    originalValue: number;
    color: string;
}

export default function PortfolioPiechart({
    chartHeight = "calc(100% - 30px)",
    data,
}: {
    chartHeight?: string;
    data: any[];
}) {
    const configMap: { [key: string]: { color: string; displayName: string } } =
        useMemo(
            () => ({
                solar: { color: "#F1935C", displayName: "Solar" },
                wind: { color: "#BCECE0", displayName: "Wind" },
                // hydro: { color: "#34656D", displayName: "Hydro" },
                thermal: { color: "#FF8894", displayName: "Thermal" },
                others: { color: "#2C515D", displayName: "Others" },
                surplus: { color: "#34656D", displayName: "Surplus" },
                deficit: { color: "#F1935C", displayName: "Deficit" },
                re_generation: { color: "#F1935C", displayName: "RE Generation" },
                non_re_generation: { color: "#34656D", displayName: "Non-RE Gen" },
            }),
            []
        );

    const dataPie: DataItem[] = useMemo(() => {
        if (data.length > 0 && data[0]) {
            return Object.keys(data[0])
                .filter((key) => configMap[key])
                .map((key) => {
                    const originalValue = data[0][key] || 0;
                    const absValue = Math.abs(originalValue);
                    return {
                        name: configMap[key]?.displayName || key.toUpperCase(),
                        value: absValue === 0 ? 0.001 : absValue,
                        originalValue: originalValue,
                        color: configMap[key]?.color || "#CCCCCC",
                    };
                })
                .sort((a, b) => b.value - a.value);
        }
        return [];
    }, [configMap, data]);

    const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
        active,
        payload,
    }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload as DataItem;
            return (
                <div
                    className="rounded-lg shadow-xl p-2"
                    style={{
                        backgroundColor: "white",
                        border: "2px solid #f3f4f6",
                        borderRadius: "12px",
                    }}
                >
                    <div className="flex items-center gap-2 mb-0">
                        <span
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: data.color }}
                        />
                        <span className="font-medium text-xs" style={{ color: data.color }}>
                            {payload[0].name}
                        </span>
                    </div>
                    <div className="text-[#34656D] text-xs">
                        {data.originalValue.toLocaleString()} MW
                    </div>
                </div>
            );
        }
        return null;
    };

    const renderCustomLabel = (props: any) => {
        const RADIAN = Math.PI / 180;
        const { cx, cy, midAngle, outerRadius, name, fill } = props;

        // Push labels out further
        const radius = outerRadius * 1.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <foreignObject x={x - 40} y={y - 12} width={100} height={30}>
                <div
                    className="flex items-center gap-1.5 bg-white px-2 py-1 rounded-md shadow-[0_2px_4px_rgba(0,0,0,0.1)] border border-gray-100 w-max"
                >
                    <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: fill }}
                    />
                    <span className="text-[10px] font-medium text-gray-600 whitespace-nowrap">
                        {name}
                    </span>
                </div>
            </foreignObject>
        );
    };

    return (
        <div style={{ width: "100%", height: chartHeight }}>
            <ResponsiveContainer>
                <PieChart>
                    <Pie
                        data={dataPie}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomLabel}
                        outerRadius={68}
                        fill="#8884d8"
                        dataKey="value"
                        strokeWidth={2}
                        stroke="#fff"
                        paddingAngle={2}
                        isAnimationActive={true}
                    >
                        {dataPie.map((entry: DataItem, index: number) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
}
